import { computed, toRef, shallowRef, ref, watch, onBeforeMount, nextTick, onBeforeUnmount, withDirectives, vShow, unref, defineComponent, inject, resolveComponent, openBlock, createBlock, normalizeProps, guardReactiveProps } from "vue";
import { u as useDefaults, _ as _export_sfc } from "./_plugin-vue_export-helper-3194dc60.mjs";
import { p as propsFactory, m as makeComponentProps, l as makeDimensionProps, a as genericComponent, s as useDimension, u as useRender, n as makeRoundedProps, y as makeTransitionProps, z as Intersect, q as useBackgroundColor, v as useRounded, g as getCurrentInstance, S as SUPPORTS_INTERSECTION, h as convertToUnit, M as MaybeTransition, A as useColor, B as getCurrentInstanceName, I as IconValue, k as makeDensityProps, C as makeSizeProps, b as makeTagProps, e as makeThemeProps, f as provideTheme, r as useDensity, D as useSize, F as VIcon, V as VDefaultsProvider } from "./_density-8094aebe.mjs";
const _VAvatar = "";
const _VImg = "";
const _VResponsive = "";
function useAspectStyles(props) {
  return {
    aspectStyles: computed(() => {
      const ratio = Number(props.aspectRatio);
      return ratio ? { paddingBottom: String(1 / ratio * 100) + "%" } : void 0;
    })
  };
}
const makeVResponsiveProps = propsFactory({
  aspectRatio: [String, Number],
  contentClass: null,
  inline: Boolean,
  ...makeComponentProps(),
  ...makeDimensionProps()
}, "VResponsive");
const VResponsive = genericComponent()({
  name: "VResponsive",
  props: makeVResponsiveProps(),
  setup(props, { slots }) {
    const { aspectStyles } = useAspectStyles(props);
    const { dimensionStyles } = useDimension(props);
    useRender(() => {
      var _a;
      return /* @__PURE__ */ React.createElement(
        "div",
        {
          class: [
            "v-responsive",
            { "v-responsive--inline": props.inline },
            props.class
          ],
          style: [
            dimensionStyles.value,
            props.style
          ]
        },
        /* @__PURE__ */ React.createElement("div", { class: "v-responsive__sizer", style: aspectStyles.value }),
        (_a = slots.additional) == null ? void 0 : _a.call(slots),
        slots.default && /* @__PURE__ */ React.createElement("div", { class: ["v-responsive__content", props.contentClass] }, slots.default())
      );
    });
    return {};
  }
});
const makeVImgProps = propsFactory({
  alt: String,
  cover: Boolean,
  color: String,
  draggable: {
    type: [Boolean, String],
    default: void 0
  },
  eager: Boolean,
  gradient: String,
  lazySrc: String,
  options: {
    type: Object,
    // For more information on types, navigate to:
    // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
    default: () => ({
      root: void 0,
      rootMargin: void 0,
      threshold: void 0
    })
  },
  sizes: String,
  src: {
    type: [String, Object],
    default: ""
  },
  crossorigin: String,
  referrerpolicy: String,
  srcset: String,
  position: String,
  ...makeVResponsiveProps(),
  ...makeComponentProps(),
  ...makeRoundedProps(),
  ...makeTransitionProps()
}, "VImg");
const VImg = genericComponent()({
  name: "VImg",
  directives: { intersect: Intersect },
  props: makeVImgProps(),
  emits: {
    loadstart: (value) => true,
    load: (value) => true,
    error: (value) => true
  },
  setup(props, { emit, slots }) {
    const { backgroundColorClasses, backgroundColorStyles } = useBackgroundColor(toRef(props, "color"));
    const { roundedClasses } = useRounded(props);
    const vm = getCurrentInstance("VImg");
    const currentSrc = shallowRef("");
    const image = ref();
    const state = shallowRef(props.eager ? "loading" : "idle");
    const naturalWidth = shallowRef();
    const naturalHeight = shallowRef();
    const normalisedSrc = computed(() => {
      return props.src && typeof props.src === "object" ? {
        src: props.src.src,
        srcset: props.srcset || props.src.srcset,
        lazySrc: props.lazySrc || props.src.lazySrc,
        aspect: Number(props.aspectRatio || props.src.aspect || 0)
      } : {
        src: props.src,
        srcset: props.srcset,
        lazySrc: props.lazySrc,
        aspect: Number(props.aspectRatio || 0)
      };
    });
    const aspectRatio = computed(() => {
      return normalisedSrc.value.aspect || naturalWidth.value / naturalHeight.value || 0;
    });
    watch(() => props.src, () => {
      init(state.value !== "idle");
    });
    watch(aspectRatio, (val, oldVal) => {
      if (!val && oldVal && image.value) {
        pollForSize(image.value);
      }
    });
    onBeforeMount(() => init());
    function init(isIntersecting) {
      if (props.eager && isIntersecting)
        return;
      if (SUPPORTS_INTERSECTION && !isIntersecting && !props.eager)
        return;
      state.value = "loading";
      if (normalisedSrc.value.lazySrc) {
        const lazyImg = new Image();
        lazyImg.src = normalisedSrc.value.lazySrc;
        pollForSize(lazyImg, null);
      }
      if (!normalisedSrc.value.src)
        return;
      nextTick(() => {
        var _a;
        emit("loadstart", ((_a = image.value) == null ? void 0 : _a.currentSrc) || normalisedSrc.value.src);
        setTimeout(() => {
          var _a2;
          if (vm.isUnmounted)
            return;
          if ((_a2 = image.value) == null ? void 0 : _a2.complete) {
            if (!image.value.naturalWidth) {
              onError();
            }
            if (state.value === "error")
              return;
            if (!aspectRatio.value)
              pollForSize(image.value, null);
            if (state.value === "loading")
              onLoad();
          } else {
            if (!aspectRatio.value)
              pollForSize(image.value);
            getSrc();
          }
        });
      });
    }
    function onLoad() {
      var _a;
      if (vm.isUnmounted)
        return;
      getSrc();
      pollForSize(image.value);
      state.value = "loaded";
      emit("load", ((_a = image.value) == null ? void 0 : _a.currentSrc) || normalisedSrc.value.src);
    }
    function onError() {
      var _a;
      if (vm.isUnmounted)
        return;
      state.value = "error";
      emit("error", ((_a = image.value) == null ? void 0 : _a.currentSrc) || normalisedSrc.value.src);
    }
    function getSrc() {
      const img = image.value;
      if (img)
        currentSrc.value = img.currentSrc || img.src;
    }
    let timer = -1;
    onBeforeUnmount(() => {
      clearTimeout(timer);
    });
    function pollForSize(img, timeout = 100) {
      const poll = () => {
        clearTimeout(timer);
        if (vm.isUnmounted)
          return;
        const { naturalHeight: imgHeight, naturalWidth: imgWidth } = img;
        if (imgHeight || imgWidth) {
          naturalWidth.value = imgWidth;
          naturalHeight.value = imgHeight;
        } else if (!img.complete && state.value === "loading" && timeout != null) {
          timer = window.setTimeout(poll, timeout);
        } else if (img.currentSrc.endsWith(".svg") || img.currentSrc.startsWith("data:image/svg+xml")) {
          naturalWidth.value = 1;
          naturalHeight.value = 1;
        }
      };
      poll();
    }
    const containClasses = computed(() => ({
      "v-img__img--cover": props.cover,
      "v-img__img--contain": !props.cover
    }));
    const __image = () => {
      var _a;
      if (!normalisedSrc.value.src || state.value === "idle")
        return null;
      const img = /* @__PURE__ */ React.createElement(
        "img",
        {
          class: ["v-img__img", containClasses.value],
          style: { objectPosition: props.position },
          src: normalisedSrc.value.src,
          srcset: normalisedSrc.value.srcset,
          alt: props.alt,
          crossorigin: props.crossorigin,
          referrerpolicy: props.referrerpolicy,
          draggable: props.draggable,
          sizes: props.sizes,
          ref: image,
          onLoad,
          onError
        }
      );
      const sources = (_a = slots.sources) == null ? void 0 : _a.call(slots);
      return /* @__PURE__ */ React.createElement(MaybeTransition, { transition: props.transition, appear: true }, withDirectives(
        sources ? /* @__PURE__ */ React.createElement("picture", { class: "v-img__picture" }, sources, img) : img,
        [[vShow, state.value === "loaded"]]
      ));
    };
    const __preloadImage = () => /* @__PURE__ */ React.createElement(MaybeTransition, { transition: props.transition }, normalisedSrc.value.lazySrc && state.value !== "loaded" && /* @__PURE__ */ React.createElement(
      "img",
      {
        class: ["v-img__img", "v-img__img--preload", containClasses.value],
        style: { objectPosition: props.position },
        src: normalisedSrc.value.lazySrc,
        alt: props.alt,
        crossorigin: props.crossorigin,
        referrerpolicy: props.referrerpolicy,
        draggable: props.draggable
      }
    ));
    const __placeholder = () => {
      if (!slots.placeholder)
        return null;
      return /* @__PURE__ */ React.createElement(MaybeTransition, { transition: props.transition, appear: true }, (state.value === "loading" || state.value === "error" && !slots.error) && /* @__PURE__ */ React.createElement("div", { class: "v-img__placeholder" }, slots.placeholder()));
    };
    const __error = () => {
      if (!slots.error)
        return null;
      return /* @__PURE__ */ React.createElement(MaybeTransition, { transition: props.transition, appear: true }, state.value === "error" && /* @__PURE__ */ React.createElement("div", { class: "v-img__error" }, slots.error()));
    };
    const __gradient = () => {
      if (!props.gradient)
        return null;
      return /* @__PURE__ */ React.createElement("div", { class: "v-img__gradient", style: { backgroundImage: `linear-gradient(${props.gradient})` } });
    };
    const isBooted = shallowRef(false);
    {
      const stop = watch(aspectRatio, (val) => {
        if (val) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isBooted.value = true;
            });
          });
          stop();
        }
      });
    }
    useRender(() => {
      const responsiveProps = VResponsive.filterProps(props);
      return /* @__PURE__ */ React.createElement(
        VResponsive,
        {
          class: [
            "v-img",
            { "v-img--booting": !isBooted.value },
            backgroundColorClasses.value,
            roundedClasses.value,
            props.class
          ],
          style: [
            { width: convertToUnit(props.width === "auto" ? naturalWidth.value : props.width) },
            backgroundColorStyles.value,
            props.style
          ],
          ...responsiveProps,
          aspectRatio: aspectRatio.value,
          "aria-label": props.alt,
          role: props.alt ? "img" : void 0,
          "v-intersect": [{
            handler: init,
            options: props.options
          }, null, ["once"]]
        },
        {
          additional: () => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(__image, null), /* @__PURE__ */ React.createElement(__preloadImage, null), /* @__PURE__ */ React.createElement(__gradient, null), /* @__PURE__ */ React.createElement(__placeholder, null), /* @__PURE__ */ React.createElement(__error, null)),
          default: slots.default
        }
      );
    });
    return {
      currentSrc,
      image,
      state,
      naturalWidth,
      naturalHeight
    };
  }
});
const allowedVariants = [
  "elevated",
  "flat",
  "tonal",
  "outlined",
  "text",
  "plain"
];
function genOverlays(isClickable, name) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, isClickable && /* @__PURE__ */ React.createElement("span", { key: "overlay", class: `${name}__overlay` }), /* @__PURE__ */ React.createElement("span", { key: "underlay", class: `${name}__underlay` }));
}
const makeVariantProps = propsFactory({
  color: String,
  variant: {
    type: String,
    default: "elevated",
    validator: (v) => allowedVariants.includes(v)
  }
}, "variant");
function useVariant(props, name = getCurrentInstanceName()) {
  const variantClasses = computed(() => {
    const { variant } = unref(props);
    return `${name}--variant-${variant}`;
  });
  const { colorClasses, colorStyles } = useColor(computed(() => {
    const { variant, color } = unref(props);
    return {
      [["elevated", "flat"].includes(variant) ? "background" : "text"]: color
    };
  }));
  return { colorClasses, colorStyles, variantClasses };
}
const makeVAvatarProps = propsFactory({
  start: Boolean,
  end: Boolean,
  icon: IconValue,
  image: String,
  text: String,
  ...makeComponentProps(),
  ...makeDensityProps(),
  ...makeRoundedProps(),
  ...makeSizeProps(),
  ...makeTagProps(),
  ...makeThemeProps(),
  ...makeVariantProps({ variant: "flat" })
}, "VAvatar");
const VAvatar = genericComponent()({
  name: "VAvatar",
  props: makeVAvatarProps(),
  setup(props, { slots }) {
    const { themeClasses } = provideTheme(props);
    const { colorClasses, colorStyles, variantClasses } = useVariant(props);
    const { densityClasses } = useDensity(props);
    const { roundedClasses } = useRounded(props);
    const { sizeClasses, sizeStyles } = useSize(props);
    useRender(() => /* @__PURE__ */ React.createElement(
      props.tag,
      {
        class: [
          "v-avatar",
          {
            "v-avatar--start": props.start,
            "v-avatar--end": props.end
          },
          themeClasses.value,
          colorClasses.value,
          densityClasses.value,
          roundedClasses.value,
          sizeClasses.value,
          variantClasses.value,
          props.class
        ],
        style: [
          colorStyles.value,
          sizeStyles.value,
          props.style
        ]
      },
      !slots.default ? props.image ? /* @__PURE__ */ React.createElement(VImg, { key: "image", src: props.image, alt: "", cover: true }) : props.icon ? /* @__PURE__ */ React.createElement(VIcon, { key: "icon", icon: props.icon }) : props.text : /* @__PURE__ */ React.createElement(
        VDefaultsProvider,
        {
          key: "content-defaults",
          defaults: {
            VImg: {
              cover: true,
              image: props.image
            },
            VIcon: {
              icon: props.icon
            }
          }
        },
        slots.default()
      ),
      genOverlays(false, "v-avatar")
    ));
    return {};
  }
});
const _sfc_main = defineComponent({
  name: "MalarkeyContactAvatar",
  props: {
    ...makeVAvatarProps(),
    identity: {
      type: String,
      default: void 0
    }
  },
  setup(props) {
    const identity = computed(() => props.identity);
    const http = inject("http");
    const image = ref(null);
    const vAvatarDefaults = useDefaults(props, "VAvatar");
    const MalarkeyContactAvatarDefaults = useDefaults(
      vAvatarDefaults,
      "MalarkeyContactAvatar"
    );
    const avatarBindings = computed(() => ({
      ...MalarkeyContactAvatarDefaults,
      image: image.value,
      icon: !image.value ? "mdi-account" : void 0,
      identity: void 0
    }));
    const getAvatar = async (id, signal) => {
      let img = null;
      const cacheName = `mca-${id}`;
      const uri = `/profile/avatar/${id}`;
      let cacheStorage;
      if (window && window.caches) {
        try {
          cacheStorage = await window.caches.open(cacheName);
        } catch {
        }
      }
      if (cacheStorage) {
        let fromCache;
        try {
          fromCache = await cacheStorage.match(uri);
        } catch {
        }
        if (fromCache && fromCache.ok) {
          try {
            img = await fromCache.text();
          } catch {
          }
        }
      }
      if (!img && http) {
        try {
          const { status, data } = await http.get(uri, {
            signal
          });
          if (200 === status) {
            img = data.data;
            if (cacheStorage) {
              try {
                await cacheStorage.put(uri, new Response(img));
              } catch {
              }
            }
          }
        } catch {
        }
      }
      return img;
    };
    let abortController;
    watch(
      () => identity.value,
      (id) => {
        if (abortController) {
          abortController.abort();
        }
        if (!id) {
          image.value = null;
          return;
        }
        abortController = new AbortController();
        getAvatar(id, abortController.signal).then((img) => {
          image.value = img;
        });
      },
      { immediate: true }
    );
    return {
      avatarBindings
    };
  }
});
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_v_avatar = resolveComponent("v-avatar");
  return openBlock(), createBlock(_component_v_avatar, normalizeProps(guardReactiveProps(_ctx.avatarBindings)), null, 16);
}
const MalarkeyContactAvatar = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render], ["__file", "MalarkeyContactAvatar.vue"]]);
export {
  MalarkeyContactAvatar as M,
  VAvatar as V,
  makeVAvatarProps as a,
  genOverlays as g,
  makeVariantProps as m,
  useVariant as u
};
//# sourceMappingURL=MalarkeyContactAvatar-1b079ba3.mjs.map
